import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '../i18n/I18nContext';
import { BFF_URL } from '../config/domains';

export default function Partners() {
  const { t, locale } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', firm: '', role: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) {
      setError(t('apply.errors.required'));
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${BFF_URL}/api/v1/advisor/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.name.split(' ')[0] || form.name,
          last_name: form.name.split(' ').slice(1).join(' ') || '—',
          email: form.email,
          firm_name: form.firm || null,
          notes: `[Partner] role=${form.role || ''} notes=${form.notes || ''}`,
          plan_tier: 'starter',
          source: 'advisor-web:partners',
          locale
        })
      });
      if (!res.ok && res.status !== 201) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || 'submit failed');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || t('apply.errors.submit'));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 md:py-20">
      <p className="landing-eyebrow">{t('partners.eyebrow')}</p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">{t('partners.title')}</h1>
      <p className="mt-4 text-sm leading-relaxed text-slate-600">{t('partners.subtitle')}</p>

      <div className="mt-10 space-y-8">
        {[
          { key: 'why', title: t('partners.why.title'), body: t('partners.why.body') },
          { key: 'how', title: t('partners.how.title'), body: t('partners.how.body') },
          { key: 'compliance', title: t('partners.compliance.title'), body: t('partners.compliance.body') }
        ].map((s) => (
          <section key={s.key}>
            <h2 className="text-lg font-semibold">{s.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
          </section>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="thanks"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6 text-sm text-coverfy-accentDark"
          >
            {t('partners.success')}
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={onSubmit}
            className="mt-10 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-diffuse"
          >
            <label className="block text-sm font-medium">
              {t('partners.fields.name')}
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm font-medium">
              {t('partners.fields.email')}
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm font-medium">
                {t('partners.fields.firm')}
                <input
                  type="text"
                  value={form.firm}
                  onChange={(e) => setForm({ ...form, firm: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-sm font-medium">
                {t('partners.fields.role')}
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </label>
            </div>
            <label className="block text-sm font-medium">
              {t('partners.fields.notes')}
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={sending}
              className="w-full rounded-full bg-coverfy-accent py-3 text-sm font-medium text-white active:scale-[0.98] disabled:opacity-50"
            >
              {sending ? '…' : t('partners.fields.submit')}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
